/**
 * Query-string utilities used by the client-side InvDataRefactored path.
 *
 * NOTE: The initial-fetch parameter preparation + sanitization
 * (processParameterMap / mergePathParameters / prepareParameterMapForInitialFetch
 * and the `applyXSSProtection` param cleaner) now live in wsm-wis-client and are
 * consumed server-side by store.js. The local copies were removed once the SSR
 * path adopted the library (TA4063704), leaving only the client query-string
 * helpers below. See wsm-wis-client for the param-cleaning contract.
 */

/**
 * Builds a query string object with optional overrides
 * Extracted from InvDataRefactored for reusability
 *
 * @param {Object} urlParams - URL parameters object
 * @param {string|Array} sortBy - Sort option (can be string or array)
 * @param {number|Array} start - Start index for pagination
 * @param {Object} overrides - Optional parameter overrides
 * @returns {Object} - Query string object with sortBy and start included if applicable
 */
export const getQueryStringObj = (
	urlParams = {},
	sortBy = '',
	start = 0,
	overrides = {}
) => {
	const params = { ...urlParams };

	if (sortBy && !sortBy.includes('')) {
		params.sortBy = sortBy;
	}
	if (start !== 0) {
		params.start = start;
	}

	return {
		...params,
		...overrides
	};
};

/**
 * Converts a facet object (or primitive) into a formatted URL query string.
 * Migrated from legacy url-params.js during cleanup of dead code paths.
 *
 * @param {Object|string|number} facetObj - Facet data to serialize
 * @returns {string} - Formatted query string (e.g., "?make=Toyota&model=Camry")
 */
export const urlizeFacetObj = (facetObj) => {
	let queryString = '';

	/* eslint-disable no-constant-condition */
	switch (typeof facetObj) {
		case 'object':
			if (typeof facetObj) {
				Object.entries(facetObj).forEach(([name, values]) => {
					values.forEach((value) => {
						if (queryString) {
							queryString += '&';
						}
						queryString += `${encodeURIComponent(
							urlizeFacetObj(name)
						)}=${encodeURIComponent(value)}`;
					});
				});
				return `${queryString.length >= 1 ? '?' : ''}${queryString}`;
			} else {
				return '';
			}
		case 'string':
			return facetObj;
		case 'number':
			return String(facetObj);
		default:
			return '';
	}
	/* eslint-enable no-constant-condition */
};

/**
 * Filters out URL params that are not inventory-related.
 * Checks against appliedFilters, removes `incentiveId`, `vdpUrl`, and SB-prefixed params.
 * Migrated from legacy url-params.js during cleanup of dead code paths.
 *
 * @param {Object} urlParams - URL parameters object
 * @param {Array} appliedFilters - Currently applied filters
 * @returns {Object} - Filtered URL parameters
 */
export const filterNonInventoryParams = (
	urlParams = {},
	appliedFilters = []
) => {
	const knownList = appliedFilters.length
		? appliedFilters.map((item) => item.id)
		: [];
	/* eslint-disable no-param-reassign */
	return Object.keys(urlParams)
		.filter(
			(key) =>
				!knownList.includes(key) &&
				key !== 'incentiveId' &&
				key !== 'vdpUrl' &&
				!key.match(/^SB.*/)
		)
		.reduce((obj, key) => {
			obj[key] = urlParams[key];
			return obj;
		}, {});
};

/**
 * Finds and returns the legacy SB-prefixed param key(s) that map to a given facet name.
 * Migrated from legacy url-params.js during cleanup of dead code paths.
 *
 * @param {string} selectedFacet - The facet name to look up
 * @returns {Array} - Matching SB-prefixed key names
 */
export const findSBParamsMappingKey = (selectedFacet = '') => {
	const legacyParamsMapping = {
		SBtype: 'compositeType',
		SByear: 'year',
		SBmake: 'make',
		SBmodel: 'model',
		SBbodystyle: 'bodyStyle',
		SBtrim: 'trim',
		SBprice: 'price',
		SBmileage: 'odometer',
		SBcityMPG: 'cityMpg',
		SBhighwayMPG: 'highwayMpg',
		SBstockNumber: 'stockNumber',
		SBlinkedAccountId: 'accountId'
	};

	return Object.keys(legacyParamsMapping).filter(
		(key) => legacyParamsMapping[key] === selectedFacet
	);
};

/**
 * Builds a formatted query string from URL parameters
 * Extracted from InvDataRefactored for reusability
 *
 * @param {Object} urlParams - URL parameters object
 * @param {string|Array} sortBy - Sort option (can be string or array)
 * @param {number|Array} start - Start index for pagination
 * @param {Object} overrides - Optional parameter overrides
 * @returns {string} - Formatted query string (e.g., "?make=Toyota&model=Camry")
 */
export const getQueryString = (
	urlParams = {},
	sortBy = '',
	start = 0,
	overrides = {}
) => {
	return urlizeFacetObj(
		getQueryStringObj(urlParams, sortBy, start, overrides)
	);
};
